import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable, Provider } from '@angular/core';

import { PosthogService } from './posthog.service';

/**
 * ErrorHandler que autocaptura en PostHog las excepciones no manejadas de Angular.
 *
 * Desenvuelve el error real (Zone.js lo envuelve) y trata los `HttpErrorResponse`
 * para mandar un mensaje útil. La captura se delega al `PosthogService`, que ya
 * corre fuera de zona y respeta el guard de init. Se registra vía `useClass` para
 * que Angular lo instancie por DI y pueda inyectar el service.
 */
@Injectable({ providedIn: 'root' })
export class PostHogErrorHandler implements ErrorHandler {
  private readonly _posthog = inject(PosthogService);

  handleError(error: unknown): void {
    const extractedError = this._extractError(error) || 'Unknown error';
    this._posthog.captureException(extractedError);
  }

  private _extractError(errorCandidate: unknown): unknown {
    const error = tryToUnwrapZonejsError(errorCandidate);
    if (error instanceof HttpErrorResponse) {
      return extractHttpModuleError(error);
    }
    if (typeof error === 'string' || isErrorOrErrorLikeObject(error)) {
      return error;
    }
    return null;
  }
}

function tryToUnwrapZonejsError(error: unknown): unknown | Error {
  return error && (error as { ngOriginalError: Error }).ngOriginalError
    ? (error as { ngOriginalError: Error }).ngOriginalError
    : error;
}

function extractHttpModuleError(error: HttpErrorResponse): string | Error {
  if (isErrorOrErrorLikeObject(error.error)) {
    return error.error;
  }
  if (typeof ErrorEvent !== 'undefined' && error.error instanceof ErrorEvent && error.error.message) {
    return error.error.message;
  }
  if (typeof error.error === 'string') {
    return `Server returned code ${error.status} with body "${error.error}"`;
  }
  return error.message;
}

function isErrorOrErrorLikeObject(value: unknown): value is Error {
  if (value instanceof Error) {
    return true;
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  return 'name' in value && 'message' in value && 'stack' in value;
}

/** Registra el ErrorHandler de PostHog. Agregar a los `providers` del `app.config.ts`. */
export function providePostHogErrorHandler(): Provider {
  return {
    provide: ErrorHandler,
    useClass: PostHogErrorHandler,
  };
}
